import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { isLocalMode } from "../../lib/appMode";
import { useQuery } from "@tanstack/react-query";
import { getSource, useSources } from "../../sources";
import { ServerSource } from "../../sources/serverSource";
import { useWatchSnapshot } from "../../components/media";
import { DetailHero, circleAction } from "../../components/library/detail-hero";
import { ItemMenu } from "../../components/library/item-menu";
import { TechInfo } from "../../components/library/tech-info";
import { SeasonsSection, EpisodesSection } from "../../components/library/season-browser";
import { BackCrumb } from "../../components/library/back-crumb";
import { CastRow, useTmdbDetail } from "../../components/library/cast-row";
import { DirectorsByline } from "../../components/library/directors-byline";
import { ChaptersStrip } from "../../components/library/chapters-strip";
import { formatOffset } from "../../components/library/media-meta";
import { BookmarkIcon, CheckIcon, PlayIcon, RestartIcon } from "../../components/icons";
import { LoadingState, NotFoundState } from "../../components/page-state";
import { useNextUnwatched } from "../../lib/episodeNav";
import { isResumable, showsWatched } from "../../lib/playState";
import { useAmbientBackdrop } from "../../lib/pageBackdrop";
import { useSourceConnection } from "../../lib/useConnection";
import { isOnWatchlist, useWatchlist, useWatchlistToggle } from "../../lib/watchlist";
import type { MediaItem, RagoSource } from "../../sources";

export const Route = createFileRoute("/_authed/item/$sourceId/$itemId")({
  component: ItemDetail,
});

/**
 * The unified detail page: one route, four types. /v1/items/{id} serves
 * movies, shows, seasons and episodes alike, so the page just dispatches —
 * shows get a Seasons section, seasons an episode grid, movies and episodes
 * the play actions. Season and episode pages borrow the show's backdrop
 * (they have none of their own) and link back up the hierarchy. Cast and
 * the director byline are bridged from the Discovery TMDB proxy via the
 * item's tmdb external id (see cast-row.tsx).
 */
function ItemDetail() {
  // An art route: the shell's backdrop carries the last art seen while
  // nothing of ours has decoded (lib/pageBackdrop) — first hook, before any
  // early return, so the spinner phase has it too.
  useAmbientBackdrop();
  const { sourceId, itemId } = Route.useParams();
  // Drive/await the source registry so a direct load doesn't 404 before the
  // server's ServerSource is registered.
  const { isLoading: sourcesLoading } = useSources();
  const source = getSource(sourceId);
  useWatchSnapshot();
  // Rebuild image srcs when the connection/token settles or flips.
  useSourceConnection(source);

  const item = useQuery({
    queryKey: ["item", sourceId, itemId],
    queryFn: async () => {
      const found = await source?.getItem(itemId);
      if (!found) throw notFound();
      return found;
    },
    enabled: Boolean(source),
  });

  // Season/episode pages fetch their show for the backdrop + breadcrumb.
  const it = item.data;
  const showId =
    it?.type === "season"
      ? (it.parentId ?? null)
      : it?.type === "episode"
        ? (it.episode?.grandparentId ?? null)
        : null;
  const show = useQuery({
    queryKey: ["item", sourceId, showId ?? "none"],
    queryFn: () => source!.getItem(showId as string),
    enabled: Boolean(source && showId),
  });
  const tmdb = useTmdbDetail(it, show.data ?? null);

  if (!source) {
    // Registry may still be hydrating on a fresh load — wait before 404-ing.
    if (sourcesLoading) return <LoadingState />;
    return <NotFoundState />;
  }
  if (item.isLoading) return <LoadingState />;
  if (item.isError || !it) return <NotFoundState />;

  const isServer = source instanceof ServerSource;
  const showItem = show.data ?? null;
  const backdropUrl = showId && showItem ? source.imageUrl(showItem, "backdrop", 1920) : undefined;

  const byline =
    it.type === "movie" || it.type === "show" ? (
      <DirectorsByline
        verb={it.type === "movie" ? "Directed by" : "Created by"}
        directors={tmdb.data?.directors}
        fallback={tmdb.data?.director ?? null}
      />
    ) : null;

  const attribution = it.attribution ? (
    <p className="border-t border-[var(--rago-line-1)] pt-4 text-xs leading-relaxed text-[var(--rago-text-3)]">
      {it.attribution.holder} · Licensed{" "}
      <a
        href={it.attribution.licenseUrl}
        target="_blank"
        rel="noreferrer"
        className="rago-link underline"
      >
        {it.attribution.license}
      </a>{" "}
      ·{" "}
      <a
        href={it.attribution.sourceUrl}
        target="_blank"
        rel="noreferrer"
        className="rago-link underline"
      >
        About this film
      </a>
    </p>
  ) : null;

  if (it.type === "show") {
    return (
      <DetailHero
        item={it}
        source={source}
        kicker={<BackCrumb />}
        byline={byline}
        score={tmdb.data?.voteAverage}
        actions={
          <>
            {isServer ? <PlayNextAction source={source} rootId={it.id} /> : null}
            {isLocalMode ? null : <WatchlistAction item={it} />}
            <ItemMenu source={source} item={it} />
          </>
        }
        below={
          <div className="rago-detail-below">
            {isServer ? (
              <div className="px-6 sm:px-10">
                <SeasonsSection source={source} showId={it.id} />
              </div>
            ) : null}
            <CastRow detail={tmdb.data} />
            {attribution ? <div className="px-6 sm:px-10">{attribution}</div> : null}
          </div>
        }
      />
    );
  }

  if (it.type === "season") {
    // Plex composition: the SHOW title leads (linked up the hierarchy),
    // "Season N" beneath it, and the series overview stands in when the
    // season has no summary of its own. Until the show loads the hero
    // falls back to the season item alone (title "Season N", no subtitle).
    return (
      <DetailHero
        item={it}
        source={source}
        backdropUrl={backdropUrl}
        remember={showItem ? { sourceId: source.id, item: showItem } : undefined}
        kicker={<BackCrumb />}
        title={
          showItem && showId ? (
            <Link
              to="/item/$sourceId/$itemId"
              params={{ sourceId, itemId: showId }}
              className="text-[0.8em] no-underline hover:underline decoration-2 underline-offset-8"
            >
              {showItem.title}
            </Link>
          ) : undefined
        }
        subtitle={showItem ? (it.index != null ? `Season ${it.index}` : it.title) : undefined}
        summary={it.summary || showItem?.summary || undefined}
        actions={
          <>
            {isServer ? <PlayNextAction source={source} rootId={it.id} /> : null}
            <ItemMenu source={source} item={it} />
          </>
        }
        below={
          <div className="rago-detail-below">
            {isServer ? (
              <div className="px-6 sm:px-10">
                <EpisodesSection source={source} seasonId={it.id} />
              </div>
            ) : null}
            <CastRow detail={tmdb.data} />
          </div>
        }
      />
    );
  }

  if (it.type === "episode") {
    // Same composition as the season page: the SHOW title leads (linked),
    // the episode title beneath. grandparentTitle stands in before the
    // show item resolves, so there's no cold-load flash.
    const showTitle = showItem?.title ?? it.episode?.grandparentTitle ?? null;
    return (
      <DetailHero
        item={it}
        source={source}
        backdropUrl={backdropUrl}
        remember={showItem ? { sourceId: source.id, item: showItem } : undefined}
        kicker={<BackCrumb />}
        title={
          showTitle && showId ? (
            <Link
              to="/item/$sourceId/$itemId"
              params={{ sourceId, itemId: showId }}
              className="text-[0.8em] no-underline hover:underline decoration-2 underline-offset-8"
            >
              {showTitle}
            </Link>
          ) : undefined
        }
        subtitle={showTitle && showId ? it.title : undefined}
        actions={
          <>
            <PlayActions source={source} item={it} />
            <ItemMenu source={source} item={it} />
          </>
        }
        playTarget={{ sourceId: source.id, itemId: it.id }}
        posterFooter={<PosterProgress source={source} item={it} />}
        below={
          <div className="rago-detail-below">
            {isServer ? <ChaptersStrip source={source} item={it} /> : null}
            <CastRow detail={tmdb.data} />
          </div>
        }
      >
        <TechInfo item={it} source={source} />
      </DetailHero>
    );
  }

  return (
    <DetailHero
      item={it}
      source={source}
      kicker={<BackCrumb />}
      byline={byline}
      score={tmdb.data?.voteAverage}
      actions={
        <>
          <PlayActions source={source} item={it} />
          {isLocalMode ? null : <WatchlistAction item={it} />}
          <ItemMenu source={source} item={it} />
        </>
      }
      playTarget={{ sourceId: source.id, itemId: it.id }}
      posterFooter={<PosterProgress source={source} item={it} />}
      below={
        <div className="rago-detail-below">
          {isServer ? <ChaptersStrip source={source} item={it} /> : null}
          <CastRow detail={tmdb.data} />
          {attribution ? <div className="px-6 sm:px-10">{attribution}</div> : null}
        </div>
      }
    >
      <TechInfo item={it} source={source} />
    </DetailHero>
  );
}

/** Bookmark circle: owned movies/shows toggle the app-level (TMDB-keyed)
 * watchlist through their matched external id; unmatched items get nothing. */
function WatchlistAction(props: { item: MediaItem }) {
  const { item } = props;
  const wl = useWatchlist();
  const { toggle, busyKey } = useWatchlistToggle();
  const tmdbId =
    item.type === "movie" || item.type === "show"
      ? item.externalIds.find((e) => e.provider === "tmdb")?.id
      : undefined;
  if (!tmdbId) return null;
  const mediaType = item.type === "show" ? ("tv" as const) : ("movie" as const);
  const saved = isOnWatchlist(wl.data, mediaType, tmdbId);
  return (
    <button
      type="button"
      className={circleAction}
      aria-label={saved ? "Remove from Watchlist" : "Add to Watchlist"}
      title={saved ? "Remove from Watchlist" : "Add to Watchlist"}
      aria-pressed={saved}
      disabled={busyKey !== null || wl.isLoading}
      onClick={() => void toggle(mediaType, tmdbId, saved)}
    >
      <BookmarkIcon size={18} className={saved ? "text-[var(--rago-accent)]" : undefined} />
    </button>
  );
}

/** Amber resume bar + mono readouts under the poster ("47 MIN IN"). */
function PosterProgress(props: { source: RagoSource; item: MediaItem }) {
  const { source, item } = props;
  const state = source.getUserState(item.id);
  const runtime = item.runtimeMs;
  if (!runtime || !isResumable(state, runtime)) return null;
  const frac = Math.min(1, state.viewOffsetMs / runtime);
  const minIn = Math.max(1, Math.round(state.viewOffsetMs / 60_000));
  const leftMin = Math.max(1, Math.round((runtime - state.viewOffsetMs) / 60_000));
  const hm = (mins: number, word: string) =>
    mins >= 60 ? `${Math.floor(mins / 60)} H ${mins % 60} M ${word}` : `${mins} MIN ${word}`;
  return (
    <div className="mt-3 px-1">
      <div className="rago-micro mb-1.5 flex justify-between">
        <span>{hm(minIn, "IN")}</span>
        <span>{hm(leftMin, "LEFT")}</span>
      </div>
      <div className="h-[3px] overflow-hidden rounded-full bg-[rgba(255,255,255,0.12)]">
        <div
          className="h-full rounded-full bg-[var(--rago-accent)]"
          style={{ width: `${frac * 100}%` }}
        />
      </div>
    </div>
  );
}

/** Play/Resume/Start over/Mark watched for the playable types. */
function PlayActions(props: { source: RagoSource; item: MediaItem }) {
  const { source, item } = props;
  const state = source.getUserState(item.id);
  const resumable = isResumable(state, item.runtimeMs);
  // A rewatch in progress offers "Mark watched" although the server still
  // counts the title as watched: the click is a scrobble (one more play,
  // the position cleared), which brings the check back — Plex's button.
  const watched = showsWatched(state);
  return (
    <>
      <Link
        to="/player/$sourceId/$itemId"
        params={{ sourceId: source.id, itemId: item.id }}
        className="rago-btn rago-btn-fit"
      >
        <PlayIcon size={16} className="-ml-1" />
        {resumable ? `Resume from ${formatOffset(state.viewOffsetMs)}` : "Play"}
      </Link>
      {resumable ? (
        <Link
          to="/player/$sourceId/$itemId"
          params={{ sourceId: source.id, itemId: item.id }}
          search={{ restart: true }}
          className={circleAction}
          aria-label="Start over"
          title="Start over"
        >
          <RestartIcon size={18} />
        </Link>
      ) : null}
      <button
        onClick={() => source.setWatched(item.id, !watched)}
        className={circleAction}
        aria-label={watched ? "Mark unwatched" : "Mark watched"}
        title={watched ? "Watched" : "Mark watched"}
        aria-pressed={watched}
      >
        <CheckIcon size={18} className={watched ? "text-[var(--rago-ok)]" : undefined} />
      </button>
    </>
  );
}

/** A show/season Play button targeting the next unwatched episode. */
function PlayNextAction(props: { source: RagoSource; rootId: string }) {
  const { next, isLoading } = useNextUnwatched(props.source, props.rootId, true);
  if (isLoading) {
    return (
      <span aria-disabled className="rago-btn rago-btn-fit opacity-60">
        <PlayIcon size={16} className="-ml-1" />
        Play
      </span>
    );
  }
  if (!next) return null;
  const tag = [
    next.seasonIndex != null ? `S${next.seasonIndex}` : null,
    next.index != null ? `E${next.index}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <Link
      to="/player/$sourceId/$itemId"
      params={{ sourceId: props.source.id, itemId: next.id }}
      className="rago-btn rago-btn-fit"
    >
      <PlayIcon size={16} className="-ml-1" />
      {next.resume ? `Resume ${tag || next.title}` : tag ? `Play ${tag}` : "Play"}
    </Link>
  );
}
